<div class="page">
  <!-- ===================== HEADER ===================== -->
  <div class="page-head">
    <div>
      <h2>{{ pageTitle() || 'Member Lookup' }}</h2>
      <p>Find an insured member by id, loan number, unique id, series no or mobile — with nominees &amp; full payment history.</p>
    </div>
    <!-- '/dashboard' is the role dispatcher. Pointing straight at /partner-dashboard sent an
         admin — who reaches this page too — to the PARTNER dashboard. -->
    <p-button label="Back to Dashboard" icon="pi pi-arrow-left" [outlined]="true" routerLink="/dashboard" />
  </div>

  <!-- ===================== SEARCH ===================== -->
  <div class="card search-card">
    <div class="search-row">
      <p-iconfield iconPosition="left" class="search-field">
        <p-inputicon styleClass="pi pi-search" />
        <input pInputText type="text" [disabled]="advancedOpen()"
          placeholder="Search by member id / loan no / unique id / series no / mobile..."
          [ngModel]="query()" (ngModelChange)="query.set($event)" (keyup.enter)="search()" />
      </p-iconfield>
      @if (isAdmin) {
        <p-select class="partner-sel" [ngModel]="selPartnerId()" (ngModelChange)="selPartnerId.set($event)"
          [options]="partnerOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label"
          [showClear]="true" placeholder="All partners" appendTo="body" />
      }
      <p-button label="Go" icon="pi pi-search" (onClick)="search()" [loading]="loading()" />
      <p-button [label]="advancedOpen() ? 'Hide' : 'Advanced'"
        [icon]="advancedOpen() ? 'pi pi-chevron-up' : 'pi pi-sliders-h'" [text]="true" (onClick)="toggleAdvanced()" />
    </div>

    @if (advancedOpen()) {
      <div class="advanced">
        <div class="adv-field">
          <label>Member Id</label>
          <input pInputText type="number" [ngModel]="advMemberId()" (ngModelChange)="advMemberId.set($event)"
            (keyup.enter)="search()" placeholder="e.g. 321" />
        </div>
        <div class="adv-field">
          <label>Loan Number</label>
          <input pInputText type="text" [ngModel]="advLoanNumber()" (ngModelChange)="advLoanNumber.set($event)"
            (keyup.enter)="search()" placeholder="LN123" />
        </div>
        <div class="adv-field">
          <label>Unique Id</label>
          <input pInputText type="text" [ngModel]="advUniqueId()" (ngModelChange)="advUniqueId.set($event)"
            (keyup.enter)="search()" placeholder="MEM2025..." />
        </div>
        <div class="adv-field">
          <label>Mobile</label>
          <input pInputText type="text" [ngModel]="advMobile()" (ngModelChange)="advMobile.set($event)"
            (keyup.enter)="search()" placeholder="9876543210" />
        </div>
        <small class="hint-muted span-all">Advanced does an exact-field search — fill any one (or more) of the above.</small>
      </div>
    }
  </div>

  <!-- ===================== RESULTS ===================== -->
  @if (loading()) {
    <div class="card pad"><div class="state"><i class="pi pi-spin pi-spinner"></i><p>Searching…</p></div></div>
  } @else if (results().length) {
    <div class="result-count">{{ results().length }} match{{ results().length > 1 ? 'es' : '' }} found</div>

    <p-accordion [value]="accordionValue()" (valueChange)="accordionValue.set($any($event))" [multiple]="true">
      @for (r of results(); track r.member.id; let i = $index) {
        <p-accordion-panel [value]="i">
          <p-accordion-header>
            <div class="acc-head">
              <span class="avatar">{{ initials(r.member.fullName) }}</span>
              <div class="acc-meta">
                <span class="acc-name">{{ r.member.fullName || '—' }}</span>
                <span class="acc-sub mono">{{ r.member.memberSeriesNo || r.member.memberUniqueId || ('#' + r.member.id) }}</span>
              </div>
              <div class="acc-tags">
                <p-tag [value]="r.member.paymentStatus || '—'" [severity]="paySeverity(r.member.paymentStatus)" />
                <p-tag [value]="r.member.kycStatus || '—'" [severity]="kycSeverity(r.member.kycStatus)" />
              </div>
            </div>
          </p-accordion-header>

          <p-accordion-content>
            <div class="detail-grid">
              <!-- Member details -->
              <section class="detail-col">
                <h4>Member Details</h4>
                <div class="kv-grid">
                  <div><span>Unique Id</span><strong class="mono">{{ r.member.memberUniqueId || '—' }}</strong></div>
                  <div><span>Loan Number</span><strong>{{ r.member.loanNumber || '—' }}</strong></div>
                  <div><span>Policy (on loan)</span><strong>{{ r.member.masterPolicyOnLoan || '—' }}</strong></div>
                  <div><span>Mobile</span><strong>{{ r.member.mobileNumber || '—' }}</strong></div>
                  <div><span>Email</span><strong>{{ r.member.email || '—' }}</strong></div>
                  <div><span>DOB / Age</span><strong>{{ r.member.dateOfBirth ? (r.member.dateOfBirth | indDate) : '—' }} <em>({{ r.member.age }})</em></strong></div>
                  <div><span>Gender</span><strong>{{ r.member.gender || '—' }}</strong></div>
                  <div><span>PAN</span><strong class="mono">{{ r.member.panNumber || '—' }}</strong></div>
                  <div><span>Aadhaar</span><strong class="mono">{{ r.member.aadhaarNumber || '—' }}</strong></div>
                  <div class="span-all"><span>Address</span><strong>{{ addr(r.member) }}</strong></div>
                  <div><span>Policy</span><strong>{{ r.member.masterPolicyName || '—' }}</strong></div>
                  @if (r.member.policyStartDate || r.member.policyEndDate) {
                    <div><span>Policy Period</span><strong>{{ r.member.policyStartDate ? (r.member.policyStartDate | indDate) : '—' }} – {{ r.member.policyEndDate ? (r.member.policyEndDate | indDate) : '—' }}</strong></div>
                  }
                </div>

                <h5>Premium Breakup</h5>
                <div class="premium-rows">
                  <div><span>Base Premium</span><strong>{{ inr(r.member.basePrimume) }}</strong></div>
                  <div><span>Doctor Consultant</span><strong>{{ inr(r.member.doctorConsultantAMT) }}</strong></div>
                  <div><span>GST ({{ r.member.gstPer }}%)</span><strong>{{ inr(r.member.gstAmt) }}</strong></div>
                  <div class="tot"><span>Premium with GST</span><strong>{{ inr(r.member.premiumWithGST) }}</strong></div>
                  <div><span>Gross Amount</span><strong>{{ inr(r.member.grossAmt) }}</strong></div>
                </div>
              </section>

              <!-- Payment summary -->
              <section class="detail-col">
                <h4>Payment Summary</h4>
                <div class="sum-cards">
                  <div class="sum-card"><span>Premium Due</span><strong>{{ inr(r.paymentSummary.premiumDue) }}</strong></div>
                  <div class="sum-card"><span>Total Paid</span><strong class="green">{{ inr(r.paymentSummary.totalPaid) }}</strong></div>
                  <div class="sum-card">
                    <span>Balance</span>
                    <strong [class.red]="r.paymentSummary.balance > 0" [class.green]="r.paymentSummary.balance <= 0">{{ inr(r.paymentSummary.balance) }}</strong>
                  </div>
                </div>
                <div class="sum-status">
                  <p-tag [value]="r.paymentSummary.paymentStatus || '—'" [severity]="paySeverity(r.paymentSummary.paymentStatus)" />
                </div>
                <div class="sum-counters">
                  <span class="cnt"><i class="pi pi-list"></i> {{ r.paymentSummary.totalTransactions }} txns</span>
                  <span class="cnt ok"><i class="pi pi-check-circle"></i> {{ r.paymentSummary.successCount }}</span>
                  <span class="cnt bad"><i class="pi pi-times-circle"></i> {{ r.paymentSummary.failedCount }}</span>
                  <span class="cnt warn"><i class="pi pi-clock"></i> {{ r.paymentSummary.pendingCount }}</span>
                </div>
                <div class="sum-last">Last paid: <strong>{{ r.paymentSummary.lastPaidAt ? (r.paymentSummary.lastPaidAt | indDate) : '—' }}</strong></div>
              </section>
            </div>

            <p-divider />

            <!-- Nominees -->
            <h4 class="block-title">Nominees</h4>
            <p-table [value]="r.nominees" dataKey="id" styleClass="p-datatable-sm" [rows]="10"
              [paginator]="r.nominees.length > 10" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}" [rowsPerPageOptions]="[10, 25, 50]">
              <ng-template pTemplate="header">
                <tr>
                  <th>Name</th>
                  <th>Relationship</th>
                  <th>DOB</th>
                  <th class="ta-r">Share %</th>
                  <th>Mobile</th>
                  <th>Email</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-n>
                <tr>
                  <td class="name">{{ n.nomineeName || '—' }}</td>
                  <td>{{ n.relationship || '—' }}</td>
                  <td>{{ n.dateOfBirth ? (n.dateOfBirth | indDate) : '—' }}</td>
                  <td class="ta-r">{{ n.nominationPercentage }}%</td>
                  <td class="mono">{{ n.mobileNumber || '—' }}</td>
                  <td>{{ n.email || '—' }}</td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr><td colspan="6"><div class="state sm"><i class="pi pi-users"></i><p>No nominees.</p></div></td></tr>
              </ng-template>
            </p-table>

            <p-divider />

            <!-- Payment history -->
            <h4 class="block-title">Payment History</h4>
            <p-table [value]="r.payments" dataKey="id" styleClass="p-datatable-sm" [rows]="10"
              [paginator]="r.payments.length > 10" [rowsPerPageOptions]="[10, 25, 50]" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
              <ng-template pTemplate="header">
                <tr>
                  <th>Payment Uid</th>
                  <th class="ta-r">Amount</th>
                  <th>Mode</th>
                  <th>Status</th>
                  <th>Payer</th>
                  <th>Gateway</th>
                  <th>Gateway Pay Id</th>
                  <th>Failure</th>
                  <th>Paid At</th>
                  <th>Created</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-p>
                <tr class="row-click" (click)="openPaymentDetail(p)" pTooltip="Open payment details" tooltipPosition="top" [showDelay]="500">
                  <td class="mono">{{ p.paymentUniqueId || '—' }}</td>
                  <td class="ta-r amount">{{ inr(p.amount) }}</td>
                  <td>{{ p.paymentMode || '—' }}</td>
                  <td><p-tag [value]="p.paymentStatus || '—'" [severity]="paySeverity(p.paymentStatus)" /></td>
                  <td>{{ p.payerType || '—' }}</td>
                  <td>{{ p.gatewayName || '—' }}</td>
                  <td class="mono">{{ p.gatewayPaymentId || '—' }}</td>
                  <td class="reason">{{ p.failureReason || '—' }}</td>
                  <td>{{ p.paidAt ? (p.paidAt | indDate) : '—' }}</td>
                  <td>{{ p.createdDate ? (p.createdDate | indDate) : '—' }}</td>
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr><td colspan="10"><div class="state sm"><i class="pi pi-inbox"></i><p>No payments yet.</p></div></td></tr>
              </ng-template>
            </p-table>
          </p-accordion-content>
        </p-accordion-panel>
      }
    </p-accordion>
  } @else if (searched()) {
    <div class="card pad"><div class="state"><i class="pi pi-search-minus"></i><p>No matching member found.</p></div></div>
  } @else {
    <div class="card pad"><div class="state idle"><i class="pi pi-id-card"></i><p>Search for a member to view their details, nominees and payment history.</p></div></div>
  }
</div>
